import * as api from '../api';
import { SAR_ZONE_TYPE_ID } from './enum';

const DEFAULT_RANGE_IN_METER = 35000; // 35Km

function getSARZoneOfEvent(eventId) {
    return window.map
        ?.event
        ?.features
        ?.filter(feat => feat.properties?.type?.id === SAR_ZONE_TYPE_ID)
    ?? [];
}

function getMaxRange(lat,lon,sarZone) {
    if (!sarZone.features.length) { return DEFAULT_RANGE_IN_METER; }

    const bounds = L.geoJSON(sarZone).getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const se = bounds.getSouthEast();
    const nw = bounds.getNorthWest();

    const point = L.latLng(lat, lon);

    return Math.ceil(Math.max(
        point.distanceTo(sw),
        point.distanceTo(ne),
        point.distanceTo(se),
        point.distanceTo(nw)
    ));
}

function getAzimutBetweenPoints(lat1, lon1, lat2, lon2) {
    const lat1Rad = lat1 * Math.PI / 180;
    const lon1Rad = lon1 * Math.PI / 180;
    const lat2Rad = lat2 * Math.PI / 180;
    const lon2Rad = lon2 * Math.PI / 180;

    const y = Math.sin(lon2Rad - lon1Rad) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
              Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(lon2Rad - lon1Rad);

    const brng = Math.atan2(y, x) * 180 / Math.PI;
    return (brng + 360) % 360;
}

function isPointInPolygon(latlng, poly) {
    const x = latlng.lat, y = latlng.lng;
    const lls = poly.getLatLngs();
    const ring = Array.isArray(lls[0]) ? lls[0] : lls;
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = ring[i].lat, yi = ring[i].lng;
        const xj = ring[j].lat, yj = ring[j].lng;
        const intersect = ((yi > y) !== (yj > y))
            && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}

export function getAzimutData(lat, lon, sarZone) {
    if (!sarZone.features?.length) { 
        return { azimut: 0, angle: 360 }; 
    }

    const azimuths = [];
    const layers = L.geoJSON(sarZone).getLayers();
    const pt = L.latLng(lat, lon);
    let inside = false;

    layers.forEach(layer => {
        if (layer instanceof L.Polygon && !inside) {
            if (isPointInPolygon(pt, layer)) inside = true;
        }

        if (layer.getLatLng) {
            const ll = layer.getLatLng();
            azimuths.push(getAzimutBetweenPoints(lat, lon, ll.lat, ll.lng));
        } else if (layer.getLatLngs) {
            const lls = layer.getLatLngs();
            const flat = lls.flat(Infinity);
            flat.forEach(ll => {
                azimuths.push(getAzimutBetweenPoints(lat, lon, ll.lat, ll.lng));
            });
        }
    });

    if (inside) {
        return { azimut: 0, angle: 360 };
    }

    if (azimuths.length === 0) return { azimut: 0, angle: 360 };
    if (azimuths.length === 1) return { azimut: azimuths[0], angle: 0 };

    // Remove duplicates and sort
    const uniqueAzimuths = [...new Set(azimuths)].sort((a, b) => a - b);
    
    let maxGap = 0;
    let maxGapIndex = -1;
    const n = uniqueAzimuths.length;
    for (let i = 0; i < n; i++) {
        const gap = (i === n - 1) 
            ? (uniqueAzimuths[0] + 360) - uniqueAzimuths[i] 
            : uniqueAzimuths[i+1] - uniqueAzimuths[i];
        if (gap > maxGap) {
            maxGap = gap;
            maxGapIndex = i;
        }
    }
    
    const angle = 360 - maxGap;
    const azimut = (uniqueAzimuths[(maxGapIndex + 1) % n] + angle / 2) % 360;

    return {azimut: Math.round(azimut), angle: Math.ceil(angle)};
}

export function getAzimut(lat, lon, sarZone) {
    return getAzimutData(lat, lon, sarZone).azimut;
}

export function getAzimutAngle(lat, lon, sarZone) {
    return getAzimutData(lat, lon, sarZone).angle;
}

export function viewshed(lat,lon,aer,az,ht) {
    const queryParams = new URLSearchParams(window.location.search);

    if (!queryParams.has('event')) {
        console.error('No event id');
        return;
    }

    const fileName = `${aer}_${Date.now()}.geojson`;
    
    // '+' pour convertir une string en number 
    const eventId = +(queryParams.get('event'));

    const sarZone = {
        "type": "FeatureCollection", 
        "features": getSARZoneOfEvent(eventId)
    };

    const azimutData = sarZone.features.length ? getAzimutData(lat, lon, sarZone) : null;

    // On demande ici pour pas que le prompt "pollue" l'interface une fois le calcul terminé
    const displayName = prompt("Comment voulez vous appeler ce calcul ?") ?? fileName;
    
    const data = {
        "antennas": [
            {
                "id": aer, 
                "lat": lat, 
                "lon": lon, 
                "azimut": azimutData ? azimutData.azimut : az, 
                "max_range": getMaxRange(lat,lon,sarZone), 
                "target_alt": 10, 
                "observer_alt": ht,
                "azimut_angle": azimutData ? azimutData.angle : 120
            }
        ],
        "sar_zone": sarZone, 
        "input_epsg": 4326
    };
    
    return api.callServicePost('viewshed', null, data)
        .then((result) => {
            if (result?.features?.length === 1 && result.features[0]?.geometry?.coordinates?.length === 0) {
                M.toast({html: 'Aucune ligne de vue !', classes: 'orange accent-4'});
                return null;
            }

            const file = new File(
                [JSON.stringify(result)],
                fileName,
                { type: "application/geo+json" }
            );

            return api.attachFiletoEvent(eventId, file, 'trace', displayName);
        })
        .then(attached => {
            if (!attached) {
                return;
            }

            if (window.map?._eventLayer?.fetchDrawableAttachment) {
                window.map._eventLayer.fetchDrawableAttachment(attached.id);
            }

            M.toast({html: 'Calcul viewshed sauvegardé', classes: 'green accent-4'});
        })
        .catch((error) => {
            console.error("Erreur viewshed:", error);
            M.toast({html: 'Erreur sauvegarde viewshed', classes: 'red accent-4'});
            throw error;
        });
}